import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMto } from '../../context/MtoContext';
import LocationList from '../LocationList';
import '../styles/modes/customMode.css';
import { 
  isCustomMode
} from '../../Services';

import ModeHelpTooltip from './ModeHelpTooltip';
import PathSequence from './components/PathSequence';
import AvailablePoints from './components/AvailablePoints';
import PathDistance from './components/PathDistance';
import PathActions from './components/PathActions';
import PathSegmentDetails from './components/PathSegmentDetails';
import { calculatePathDistance } from './utils/pathCalculations';

// Custom mode for building paths manually
export default function CustomMode({ 
  locations, 
  startId, 
  endId,
  setLocations, 
  setStartId, 
  setEndId,
  setPath,
  path = [], 
  setMapPointSelect,
  currentModeRef,
  demand,
  modeCapacities      
}) {
  // Get connection data and metadata setter from context
  const { connections, setCustomPathMetadata } = useMto();
  // Help tooltip visibility
  const [showHelp, setShowHelp] = useState(false);
  
  // Path building state
  const [customPath, setCustomPath] = useState([]);
  const [availablePoints, setAvailablePoints] = useState([]);
  const [pathComplete, setPathComplete] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  // Toggle help display
  const toggleHelp = () => setShowHelp(!showHelp);
  
  // Initialize custom path from context when component mounts
  useEffect(() => {
    if (!initialized && locations && locations.length > 0) {
      if (path && Array.isArray(path) && path.length > 0) {
        // Restore custom path from context
        const restoredPath = path
          .map(id => locations.find(loc => loc.id === id))
          .filter(Boolean);
        
        if (restoredPath.length > 0) {
          setCustomPath(restoredPath);
          console.log('Restored path:', restoredPath.map(p => p.name));
        }
      }
      setInitialized(true);
      console.log('Custom mode initialized:', { 
        locationsCount: locations.length, 
        startId, 
        connectionsCount: connections?.length || 0,
        pathLength: path?.length || 0
      });
    }
  }, [path, locations, initialized, startId, connections]);
  
  // Reset custom path when start or end point changes
  useEffect(() => {
    if (initialized && customPath.length > 0) {
      // Check if current path still starts with startId
      const currentStart = customPath[0]?.id;
      if (startId && currentStart !== startId) {
        console.log('Start point changed, resetting custom path');
        setCustomPath([]);
        setPath([], 'Custom Path');
      }
    }
  }, [startId, endId, initialized, customPath, setPath]);

  // Initialize available points when locations change
  useEffect(() => {
    if (locations && locations.length > 0) {
      setAvailablePoints(locations.map(loc => ({ ...loc })));
      
      if (initialized && startId && customPath.length === 0) {
        // Auto-start path with start point if available (only after initialization)
        const startPoint = locations.find(loc => loc.id === startId);
        if (startPoint) {
          console.log('Auto-adding start point:', startPoint.name);
          setCustomPath([startPoint]);
          setPath([startId], 'Custom Path');
        }
      }
    } else {
      setAvailablePoints([]);
      if (customPath.length > 0) {
        setCustomPath([]);
        setPath([], 'Custom Path'); // Clear context path
      }
    }
  }, [locations, startId, setPath, initialized]);
  
  // Select next point for path
  const selectNextPoint = useCallback((point) => {
    setCustomPath(prev => {
      const newPath = [...prev, point];
      // Real-time sync to context for map rendering (simplified format)
      const pathIds = newPath.map(p => p.id);
      setPath(pathIds, 'Custom Path');
      return newPath;
    });
  }, [setPath]);
  
  // Remove point from path
  const removePointFromPath = useCallback((index) => {
    setCustomPath(prev => {
      const newPath = prev.filter((_, i) => i !== index);
      // Real-time sync to context for map rendering (simplified format)
      const pathIds = newPath.map(p => p.id);
      setPath(pathIds, 'Custom Path');
      return newPath;
    });
  }, [setPath]);
  
  // Reset path
  const resetPath = useCallback(() => {
    setCustomPath([]);
    setPathComplete(false);
    setPath([], 'Custom Path'); // Clear path in context
    
    // Auto-start with start point if available
    if (startId) {
      const startPoint = locations.find(loc => loc.id === startId);
      if (startPoint) {
        setCustomPath([startPoint]);
        setPath([startId], 'Custom Path'); // Single point doesn't need vehicle format
      }
    }
  }, [startId, locations, setPath]);
  
  // Confirm path
  const confirmPath = useCallback(() => {
    if (customPath.length >= 2) {
      const pathIds = customPath.map(point => point.id);
      setPath(pathIds, 'Custom Path');
      setPathComplete(true);
      
      // Calculate total duration with capacity constraints
      let totalDuration = 0;
      if (demand > 0 && modeCapacities) {
        for (let i = 0; i < customPath.length - 1; i++) {
          const fromPoint = customPath[i];
          const toPoint = customPath[i + 1];
          
          const connection = connections.find(conn => 
            (conn.points[0]?.id === fromPoint.id && conn.points[1]?.id === toPoint.id) ||
            (conn.points[1]?.id === fromPoint.id && conn.points[0]?.id === toPoint.id)
          );
          
          if (connection) {
            const capacity = modeCapacities[connection.transportMode] || 0;
            const segmentDuration = connection.duration || 0;
            
            if (capacity > 0) {
              const trips = Math.ceil(demand / capacity);
              totalDuration += segmentDuration * trips;
            } else {
              totalDuration += segmentDuration;
            }
          }
        }
      }
      
      // Save to context for CompareMode
      const metadataToSave = { 
        totalDuration: totalDuration > 0 ? totalDuration : parseFloat(calculatePathDistance(customPath)) || 0, 
        demand, 
        modeCapacities 
      };
      setCustomPathMetadata(metadataToSave);
    }
  }, [customPath, setPath, demand, modeCapacities, connections, setCustomPathMetadata]);
  
  
  return (
    <div className="custom-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Custom Path</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Custom Path mode"
          >
            ?
          </button>
        </div>
        <p>Create custom paths manually by selecting points and building routes.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}
      
      {/* Use updated LocationList, support vehicle count setting */}
      <LocationList 
        locations={locations}
        startId={startId}
        endId={endId}
        mode="Custom Path"
        readOnly={false}
        showActions={true}
        allowDelete={false}
        allowSetStart={true}
        allowSetEnd={true}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
        onEndIdChange={setEndId}
        showVehicleCount={false}
        showMaxCapacity={false}
      />
      
      {/* Custom Path Builder */}
      {locations.length > 0 ? (
        <div className="custom-path-builder">
          <h4>Custom Path Builder</h4>
          <p>Please select location points in order, two selection methods available: map selection and list selection.</p>
          
          <PathSequence 
            customPath={customPath}
            startId={startId}
            endId={endId}
            removePointFromPath={removePointFromPath}
          />
          
          <AvailablePoints 
            customPath={customPath}
            availablePoints={availablePoints}
            selectNextPoint={selectNextPoint}
            pathComplete={pathComplete}
            connections={connections}
            demand={demand}
            modeCapacities={modeCapacities}
          />
          
          <PathDistance 
            customPath={customPath} 
            connections={connections}
            demand={demand}
            modeCapacities={modeCapacities}
          />
          
          {/* Path segment details - capacity analysis */}
          <PathSegmentDetails 
            customPath={customPath}
            connections={connections}
            demand={demand}
            modeCapacities={modeCapacities}
          />
          
          <PathActions 
            confirmPath={confirmPath}
            resetPath={resetPath}
            customPath={customPath}
            endId={endId}
          />
        </div>
      ) : (
        <div className="no-locations-message">
          <p>Please add location points first.</p>
        </div>
      )}
    </div>
  );
} 